import { StringMap } from "../../types/common"
import ResourceManager from "../../components/ResourceManager"
import { useI18n } from "../../hooks"
import { useOptions } from "../../options/hooks/use-options"
import { TaskViewModel } from "../api/task"
import { useTaskMutations } from "../hooks/use-task-mutations"

const TaskManagersWidget = ({
  task,
  boardId,
  columnPosition,
}: {
  task: TaskViewModel
  boardId?: string
  columnPosition?: number
}) => {
  const translations = useTranslations(defaultTranslations)
  const { membershipOptions } = useOptions(task.maintainerId)
  const { addManagers, removeManagers } = useTaskMutations()
  const handleAddManagers = (ids: string[]) => {
    const managers = membershipOptions
      .filter((member) => ids.includes(member.id))
      .map((member) => ({ name: member.name, id: member.id }))
    addManagers(task.id, managers, boardId, columnPosition)
  }

  const handleRemoveManager = (id: string) => {
    const managers = task.managers
      .filter((member) => member.id === id)
      .map((member) => ({ name: member.name, id: member.id }))
    removeManagers(task.id, { managers, boardId })
  }

  const sortedMembershipOptions = membershipOptions
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  const sortedTaskManagers = task.managers
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedMembershipOptions}
      resources={sortedTaskManagers}
      onAddResources={handleAddManagers}
      onRemoveResource={handleRemoveManager}
      canAddResource={task.canUpdateManagers}
      canRemoveResource={task.canUpdateManagers}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults: Translations = defaultTranslations): Translations => {
  const { translations: t } = useI18n("task")
  const translations = (t?.managersWidget || {}) as StringMap
  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
  }
}

const defaultTranslations = {
  title: "Manager",
  emptyOptionsMessage: "No members in this organisation",
  emptyResourcesMessage: "No manager in this task",
}

type Translations = typeof defaultTranslations

export default TaskManagersWidget
